import { datadogRum } from "@datadog/browser-rum";
import { ABG_NO_DESTINATION_NOTE_FOR_RA } from "../app-consts/appConstants";
import { isABGPartner } from "./isABGPartner";

export type Destination = any;

export const ABG_TOW_RULES = {
  maxDistanceMiles: 50,
  minHoursOpen: 3,
} as const;

function parseClockTime(time: string): { hour: number; minute: number } | null {
  const [hour, minute] = time.split(":").map(Number);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }

  return { hour, minute };
}
/**
1. It checks whether the store is actually open right now.
2. If it is open, it returns the time it will close.
3. If it is not open, it returns nothing.
 */
function getCurrentOpenWindow(
  openFrom: string,
  closeAt: string,
  now = new Date()
): { closeTime: Date } | null {
  // Convert the raw clock strings into numeric hour/minute values first.
  const open = parseClockTime(openFrom);
  const close = parseClockTime(closeAt);

  // If either value is invalid, we cannot safely evaluate the schedule.
  if (!open || !close) {
    return null;
  }

  // Build today's opening timestamp using the current date plus the opening time.
  const openTimeToday = new Date(now);
  openTimeToday.setHours(open.hour, open.minute, 0, 0);

  // Build today's closing timestamp using the current date plus the closing time.
  const closeTimeToday = new Date(now);
  closeTimeToday.setHours(close.hour, close.minute, 0, 0);

  // Example: open = 10:00 PM, close = 6:00 AM, now = 1:00 AM.
  // In this case, the close time is earlier than the open time on the same day,
  // which means the business hours cross midnight and should be treated as an
  // overnight window that ends the next calendar day.
  // Move the close time forward one day so the window becomes valid.
  if (closeTimeToday <= openTimeToday) {
    closeTimeToday.setDate(closeTimeToday.getDate() + 1);
  }

  // If the current time falls inside today's window, the store is open now.
  if (now >= openTimeToday && now < closeTimeToday) {
    return { closeTime: closeTimeToday };
  }

  // Example: open = 10:00 PM yesterday, close = 6:00 AM today, now = 1:00 AM today.
  // We also check the previous-day version of the same overnight window because
  // after midnight the active shift may have started the night before, and we still
  // need to recognize that the store is open right now.
  // Shift both timestamps back one day so we can test yesterday's overnight window.
  const openTimePreviousDay = new Date(openTimeToday);
  openTimePreviousDay.setDate(openTimePreviousDay.getDate() - 1);
  const closeTimePreviousDay = new Date(closeTimeToday);
  closeTimePreviousDay.setDate(closeTimePreviousDay.getDate() - 1);

  // If the current time falls inside yesterday's overnight window, return that close time.
  if (now >= openTimePreviousDay && now < closeTimePreviousDay) {
    return { closeTime: closeTimePreviousDay };
  }

  return null;//store is close
}

function getCurrentClosingTime(
  openFrom: string,
  closeAt: string,
  now = new Date()
): Date | null {
  const openWindow = getCurrentOpenWindow(openFrom, closeAt, now);

  if (!openWindow) {
    return null;
  }

  return openWindow.closeTime;
}

export function getNearestOpenDestinationsForABG(
  destinations: Destination[] = []
): Destination[] {
  const { maxDistanceMiles, minHoursOpen } = ABG_TOW_RULES;

  const now = new Date();
  const minClosingTime = new Date(
    now.getTime() + minHoursOpen * 60 * 60 * 1000
  );

  return destinations.filter((d) => {
    if (!d?.opening_hours?.openfrom || !d?.opening_hours?.closeat || !d?.towInfo) {
      return false;
    }

    const closingTime = getCurrentClosingTime(
      d.opening_hours.openfrom,
      d.opening_hours.closeat,
      now
    );
    if (!closingTime) return false;

    const distanceMiles = Number(d.towInfo.distanceFromCurrentLocation);

    return (
      d.towInfo.distanceUnits === "mile" &&
      Number.isFinite(distanceMiles) &&
      distanceMiles <= maxDistanceMiles &&
      closingTime >= minClosingTime
    );
  });
}

export const isDestinationEmpty = (destination: any): boolean => {
  if (!destination) {
    return true;
  }

  if (typeof destination !== 'object' || Object.keys(destination).length === 0) {
    return true;
  }
  // Check if all essential address fields are missing or empty
  const addressLine1 = destination.addressLine1 ;
  const city = destination.city;
  const state = destination.state;
  if (!addressLine1 && !city && !state) {
    return true;
  }
  return false;
};

export const shouldAllowRescueSubmit = (
  partnerCode : string,
  serviceType : string,
  serviceRequestPayloadComment : string,
  destinationAddress : any,
  readyToSubmitRescue : boolean
): boolean => {
  const isABG = isABGPartner(partnerCode);
  const isTowService = serviceType === "tow";
  const isRACommentAdded =
    isABG &&
    serviceRequestPayloadComment?.includes(
      ABG_NO_DESTINATION_NOTE_FOR_RA
    );

  const isDestinationAddressFound = !isDestinationEmpty(destinationAddress);

  // If not ABG or not tow service → allow
  if (!isABG || !isTowService) {
    return true;
  }

  // If ABG partner and RA comment present but destination missing → block submit
  if ((!isRACommentAdded && !isDestinationAddressFound) || (isRACommentAdded && isDestinationAddressFound)) {
    datadogRum.addError(
                "Error RA comment added but the destination is missing for ABG partner"
              );
    return false;
  }

  return readyToSubmitRescue;
};